import { useContext, useEffect, useMemo, useState } from "react";

import { useParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { AzureConfigurator } from "~/services/azure-configurator/azure-configurator";
import { DyCampaignName } from "~/utils/dynamic-yield/types";

import { DynamicYieldContext } from "../../context/dynamic-yield/context";

export const useSwapCCAndStoreDyCampaignData = () => {
  const dyContext = useContext(DynamicYieldContext);
  // get desicions from Dynamic Yield context
  const dyChooseResponseData = dyContext?.chooseResponseData;

  // get DY desicions for the specific campaign
  const campaignDecision = dyChooseResponseData?.choices?.find(
    (choice) => choice.name === DyCampaignName.SwapCCAndStoreStockOrder,
  );

  // Check if user is included in the campaign and if there is a valid variant
  const isCampaignActive = campaignDecision?.type === "DECISION";

  const { locale } = useParams<GlobalPathParams>();
  const [
    shouldChangeCCAndStoreStockStatusOrder,
    setShouldChangeCCAndStoreStockStatusOrder,
  ] = useState<boolean | undefined>(undefined);

  const isSwapCCAndStoreStockOrderEnabled = useMemo(() => {
    const isDyEnabled = Boolean(
      AzureConfigurator.getConfig(locale)?.featureFlagList?.dynamicYield?.enabled,
    );

    if (!isDyEnabled || !isCampaignActive) {
      return false;
    }

    return true;
  }, [locale, isCampaignActive]);

  useEffect(() => {
    if (!dyContext || !isSwapCCAndStoreStockOrderEnabled) {
      setShouldChangeCCAndStoreStockStatusOrder(false);
      return;
    }

    const hasValidVariant = !!campaignDecision?.variations?.length;

    // campaign is not active or there is no valid variant
    if (!isCampaignActive || !hasValidVariant) {
      setShouldChangeCCAndStoreStockStatusOrder(false);
      return;
    }

    if (isSwapCCAndStoreStockOrderEnabled && isCampaignActive && hasValidVariant) {
      // if the campaign is active and there is a valid variant, set the status based on the variant data
      const swapCCAndStoreDyTestData =
        campaignDecision?.variations[0]?.payload?.data;
      if (swapCCAndStoreDyTestData) {
        setShouldChangeCCAndStoreStockStatusOrder(
          swapCCAndStoreDyTestData.isSwapped,
        );
      } else {
        setShouldChangeCCAndStoreStockStatusOrder(false);
      }
    }
  }, [
    dyContext,
    isSwapCCAndStoreStockOrderEnabled,
    campaignDecision,
    isCampaignActive,
  ]);

  return {
    shouldChangeCCAndStoreStockStatusOrder,
  };
};
